import React, { useState } from 'react';
import type { HeaderType } from '../../types/message';

interface MessageHeaderProps {
  headerType: HeaderType;
  headerText?: string;
  headerMediaUrl?: string;
}

const ImageIcon = () => (
  <svg className="w-12 h-12 text-graphite/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const VideoIcon = () => (
  <svg className="w-12 h-12 text-graphite/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

const DocumentIcon = () => (
  <svg className="w-10 h-10 text-graphite" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const PlayButton = () => (
  <div className="absolute inset-0 flex items-center justify-center">
    <div className="bg-black/50 rounded-full p-3">
      <svg className="w-8 h-8 text-warm-white" fill="currentColor" viewBox="0 0 24 24">
        <path d="M8 5v14l11-7z" />
      </svg>
    </div>
  </div>
);

export const MessageHeader: React.FC<MessageHeaderProps> = ({
  headerType,
  headerText,
  headerMediaUrl,
}) => {
  const [mediaError, setMediaError] = useState(false);

  // TEXT header - bold text above body
  if (headerType === 'TEXT' && headerText) {
    return (
      <div className="px-3 pt-3 pb-1">
        <p className="font-bold text-base text-charcoal">{headerText}</p>
      </div>
    );
  }

  // DOCUMENT header - document icon with filename
  if (headerType === 'DOCUMENT') {
    const filename = headerMediaUrl?.split('/').pop() || 'Document';
    return (
      <a
        href={headerMediaUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-3 p-3 bg-cream hover:bg-cream transition-colors"
      >
        <div className="flex-shrink-0 w-12 h-12 bg-brick/10 rounded-lg flex items-center justify-center">
          <DocumentIcon />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-charcoal truncate">{filename}</p>
          <p className="text-xs text-graphite">Tap to open</p>
        </div>
        <svg className="w-5 h-5 text-graphite/70 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      </a>
    );
  }

  // IMAGE or VIDEO header - media display
  if ((headerType === 'IMAGE' || headerType === 'VIDEO') && headerMediaUrl) {
    if (mediaError) {
      return (
        <div className="h-[160px] bg-cream flex items-center justify-center">
          {headerType === 'VIDEO' ? <VideoIcon /> : <ImageIcon />}
        </div>
      );
    }

    return (
      <div className="relative">
        <img
          src={headerMediaUrl}
          alt={headerType === 'VIDEO' ? 'Video thumbnail' : 'Image'}
          className="w-full max-h-[200px] object-cover"
          onError={() => setMediaError(true)}
        />
        {headerType === 'VIDEO' && <PlayButton />}
      </div>
    );
  }

  return null;
};

export default MessageHeader;
